import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '@/lib/utils';
import type { DragEdge } from '@/lib/timeline-geometry';

/**
 * What everything draggable on the timeline has in common.
 *
 * Three things have a length a planner can drag: a block, a coverage window,
 * and the draft that is about to become a block. Two pieces of surface are the
 * same for all three — the strips a length is grabbed by, and the plate that
 * says what the gesture is doing — and both live here.
 *
 * The gesture itself does not and never did: `useTimelineDrag` owns the
 * threshold, the capture, the snapping and the auto-scroll, and every caller
 * already shared it. This is the surface that reaches it.
 *
 * The strips had three copies of the same markup, and the copies disagreed —
 * on the size of the grab target, on which box the bottom strip was measured
 * against, on whether the press was stopped from bubbling, and on a set of
 * corner radii that rendered nothing at all. None of those was a decision
 * anybody made; they were what three separate writings of the same fourteen
 * lines drifted into. So they are gone rather than parameterised, and what is
 * left that genuinely varies — the inset a block keeps on its right, and the
 * grip the draft wears — is two optional props.
 *
 * The plate had one copy and two callers, and a third caller that had been
 * left without one: a coverage window being dragged printed its *stored*
 * hours, so the one number the planner was watching was the one number that
 * had not moved yet.
 */

/**
 * How tall a resize strip is, and therefore how big the target is.
 *
 * Eight pixels for all three. The capsules used six, which made the smallest
 * thing on the axis also the hardest to grab — and the reason offered for it
 * was the track's narrow column, which is a width and says nothing about how
 * tall a strip should be.
 *
 * A thing too short for three of these gets a body and no edges: two strips on
 * a twenty-pixel card leave four pixels between them, which is not a target.
 * Those are re-lengthed from the panel or the editor instead. Callers gate on
 * that themselves — see `edges` in `TimelineBlock` and `CoverageCapsule` —
 * because what makes a thing resizable right now is each caller's own
 * question, and only the arithmetic is shared.
 */
export const EDGE_PX = 8;

export interface EdgePointerHandlers {
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
  /**
   * The draft's, and the reason this is a bundle rather than three props: its
   * field is always open and blur is what commits it, so a press on a strip
   * has to prevent the default or the resize creates the item it was in the
   * middle of sizing. Nothing else here needs it, and nothing else passes it.
   */
  onMouseDown?: (event: ReactMouseEvent<HTMLElement>) => void;
}

export function EdgeHandles({
  drawn,
  onGrab,
  handlers,
  className,
  grip = false,
}: {
  /**
   * The height the card is **drawn** at, which is not always its box.
   *
   * A five-minute block is ten pixels of time inside a card drawn at
   * twenty-eight, and the card is what a pointer can reach. Measuring the
   * bottom strip from here rather than pinning it to `bottom: 0` is what keeps
   * the resize cursor on the border the planner is aiming at — pinned, it sat
   * eighteen pixels above it on exactly the blocks most likely to need
   * lengthening. Where the box and the card agree, which is everywhere but a
   * block, this is the same number and the same result.
   */
  drawn: number;
  onGrab: (edge: DragEdge, event: ReactPointerEvent<HTMLElement>) => void;
  handlers: EdgePointerHandlers;
  /** Anything the caller's own card needs — a block keeps 3px on its right. */
  className?: string;
  /**
   * Whether the bottom edge says it can be pulled.
   *
   * Off for things whose length is already set: their edges are found with the
   * cursor, and a grip on every capsule on a busy day is a row of marks that
   * mean nothing in particular. On for a draft, whose length has never been
   * chosen by anyone, which is on screen for seconds, and where pulling this
   * edge is the whole of what the planner is there to do.
   */
  grip?: boolean;
}) {
  return (
    <>
      {(['top', 'bottom'] as const).map((edge) => (
        <div
          key={edge}
          data-timeline-grab={edge}
          className={cn('absolute inset-x-0 cursor-ns-resize touch-none', className)}
          style={{ top: edge === 'top' ? 0 : drawn - EDGE_PX, height: EDGE_PX }}
          /* Stopped for the capsule's sake, which sits inside both its own
             body handler and the track's sweep — either would read this press
             as something other than a resize. The block's and the draft's
             strips are siblings of their body targets rather than children, so
             nothing bubbles there and stopping is a no-op. One rule beats
             three callers each deciding whether they are the nested one. */
          onPointerDown={(event) => {
            event.stopPropagation();
            onGrab(edge, event);
          }}
          {...handlers}
        >
          {grip && edge === 'bottom' && (
            /* `primary/70` is 3.25:1 on the card — the floor the hour rules and
               the start-and-end markers are already held to for a graphic that
               carries meaning, which "this edge moves" does. */
            <span
              aria-hidden="true"
              className="pointer-events-none absolute bottom-[3px] left-1/2 h-[3px] w-8 -translate-x-1/2 rounded-full bg-primary/70"
            />
          )}
        </div>
      ))}
    </>
  );
}

/**
 * What a drag says about itself while it is happening.
 *
 * The point of the feature, not a decoration: something that silently lands on
 * a new time makes the planner check their work afterwards. Both times move
 * together and the distance travelled is stated, so the direction and the
 * amount are read rather than inferred.
 */
export interface TimelineReadout {
  /** `'6:15 PM'` — where it now starts. */
  start: string;
  /** Where it now ends, or null for a moment, which starts and ends at once. */
  end: string | null;
  /** `'30 min → 45 min'` on an edge drag, which is about length, not position. */
  length: string | null;
  /** `'+15 min'`, `'−15 min'`, or `'No change'`. */
  delta: string;
}

/**
 * That statement, drawn.
 *
 * Outside the card, so a twelve-pixel block can still say what it is doing,
 * and above everything, because for the length of a gesture it is the only
 * thing on screen that matters.
 *
 * The card underneath goes on printing its stored times throughout, which is
 * deliberate and is most of why this exists. The stored value is what a drop
 * will commit and what an abandoned gesture will leave behind, so it is not a
 * lie — but it is not the answer to "where will this land", and that question
 * is the whole reason the planner is still holding the pointer.
 *
 * Positioned against whatever contains it: a block's `<li>`, a capsule, or the
 * draft's card.
 */
export function ReadoutPlate({ readout }: { readout: TimelineReadout }) {
  return (
    <div
      data-timeline-readout
      role="status"
      className="pointer-events-none absolute left-0 top-0 z-20 min-w-max rounded-lg bg-foreground px-2 py-1 text-[0.6875rem] leading-tight tabular-nums text-background shadow-[0_4px_12px_rgba(49,51,47,0.28)]"
    >
      <p className="font-medium">
        {readout.end === null ? readout.start : `${readout.start} – ${readout.end}`}
      </p>
      <p className="text-background/80">
        {/* Points back up at the times it is describing. Decoration, and
            announced as nothing. */}
        <span aria-hidden="true">▲ </span>
        {readout.length && `${readout.length} · `}
        {readout.delta}
      </p>
    </div>
  );
}
